import React from "react";
import { Link } from "react-router-dom";
import { JsonLd } from "../components/common/JsonLd";
import { SEOHelmet } from "../components/common/SEOHelmet";
import { PATH_NETZVERBUND_SCHWARZWALD } from "../utils/constants";
import {
  UsersIcon,
  PhoneIcon,
  EnvelopeIcon,
  HeartIcon,
  SparklesIcon,
} from "@heroicons/react/24/outline";
import pageContent from "../data/pages/netzverbund-schwarzwald.json";
import { buildNetzverbundPageGraph, netzverbundContact } from "../utils/structured-data";

type LinkPart = { text: string; to?: string };

type PageSection = (typeof pageContent.sections)[number];

const ICONS: Record<string, React.ReactNode> = {
  users: <UsersIcon className="h-6 w-6" />,
  phone: <PhoneIcon className="h-6 w-6" />,
  heart: <HeartIcon className="h-6 w-6" />,
  sparkles: <SparklesIcon className="h-6 w-6" />,
};

function renderParts(parts: LinkPart[]) {
  return parts.map((part, index) =>
    part.to ? (
      <Link
        key={index}
        to={part.to}
        className="font-medium text-ergo-700 dark:text-ergo-300 underline underline-offset-2 hover:text-ergo-800 dark:hover:text-ergo-200"
      >
        {part.text}
      </Link>
    ) : (
      <React.Fragment key={index}>{part.text}</React.Fragment>
    )
  );
}

type ContentSectionProps = {
  id: string;
  title: string;
  icon: React.ReactNode;
  children: React.ReactNode;
};

const ContentSection: React.FC<ContentSectionProps> = ({ id, title, icon, children }) => (
  <section aria-labelledby={id} className="relative">
    <div className="flex items-start sm:items-center gap-4 sm:gap-6 mb-4">
      <div className="p-3 sm:p-4 rounded-xl bg-ergo-100/80 dark:bg-ergo-900/80 shadow-sm flex-shrink-0">
        <div className="text-ergo-600 dark:text-ergo-400">{icon}</div>
      </div>
      <h2
        id={id}
        className="text-xl sm:text-2xl font-semibold tracking-tight text-gray-900 dark:text-white"
      >
        {title}
      </h2>
    </div>
    <div className="relative bg-white dark:bg-gray-800/60 rounded-xl sm:rounded-2xl shadow-sm border border-gray-200/60 dark:border-gray-700/40 p-6 sm:p-8 md:p-10">
      {children}
    </div>
  </section>
);

function SectionParagraphs({ paragraphs }: { paragraphs: string[] }) {
  return (
    <>
      {paragraphs.map((paragraph, index) => (
        <p
          key={index}
          className={`text-base text-gray-600 dark:text-gray-300 leading-relaxed${
            index > 0 ? " mt-4 sm:mt-5" : ""
          }`}
        >
          {paragraph}
        </p>
      ))}
    </>
  );
}

function NetzverbundContactBox() {
  return (
    <div className="rounded-xl border border-ergo-200/60 dark:border-ergo-700/40 bg-ergo-50/60 dark:bg-ergo-900/20 p-5 sm:p-6 space-y-3 mt-4 sm:mt-5">
      <p className="text-sm font-medium text-gray-900 dark:text-white">{netzverbundContact.hoursLabel}</p>
      <p className="text-sm text-gray-600 dark:text-gray-300 leading-relaxed">{netzverbundContact.hours}</p>
      <p className="text-sm text-gray-600 dark:text-gray-300">
        Telefon:{" "}
        <a
          href={`tel:${netzverbundContact.phone.replace(/-/g, "")}`}
          className="font-medium text-ergo-700 dark:text-ergo-300 underline underline-offset-2 hover:text-ergo-800 dark:hover:text-ergo-200"
        >
          {netzverbundContact.phoneDisplay}
        </a>
      </p>
      <p className="text-sm text-gray-600 dark:text-gray-300 flex items-start gap-2">
        <EnvelopeIcon className="h-5 w-5 flex-shrink-0 mt-0.5 text-ergo-600 dark:text-ergo-400" />
        <span>
          E-Mail:{" "}
          <a
            href={`mailto:${netzverbundContact.email}`}
            className="font-medium text-ergo-700 dark:text-ergo-300 underline underline-offset-2 hover:text-ergo-800 dark:hover:text-ergo-200"
          >
            {netzverbundContact.email}
          </a>
        </span>
      </p>
      <p className="text-sm text-gray-500 dark:text-gray-400 leading-relaxed">{netzverbundContact.afterHoursNote}</p>
    </div>
  );
}

function SectionBody({ section }: { section: PageSection }) {
  return (
    <>
      {!section.linkBeforeParagraphs && section.paragraphs && (
        <SectionParagraphs paragraphs={section.paragraphs} />
      )}

      {section.linkBeforeParagraphs && section.linkPartsBefore && section.linkParts && (
        <div className="space-y-4 sm:space-y-5">
          <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
            {renderParts(section.linkPartsBefore)}
            {renderParts(section.linkParts)}
          </p>
          {section.paragraphs && <SectionParagraphs paragraphs={section.paragraphs} />}
        </div>
      )}

      {section.intro && (
        <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed mb-4">{section.intro}</p>
      )}

      {section.bullets && (
        <ul className="space-y-2">
          {section.bullets.map((item) => (
            <li key={item} className="text-base text-gray-600 dark:text-gray-300 leading-relaxed">
              • {item}
            </li>
          ))}
        </ul>
      )}

      {section.linkAfterParagraphs && section.linkParts && (
        <p className="text-base text-gray-600 dark:text-gray-300 leading-relaxed mt-4 sm:mt-5">
          {renderParts(section.linkParts)}
        </p>
      )}

      {section.showContactBox && <NetzverbundContactBox />}
    </>
  );
}

export default function NetzverbundSchwarzwaldPage() {
  return (
    <>
      <SEOHelmet page="netzverbundSchwarzwald" pathname={PATH_NETZVERBUND_SCHWARZWALD} />
      <JsonLd data={buildNetzverbundPageGraph()} />

      <main id="main-content" tabIndex={-1} className="space-y-8 sm:space-y-14">
        <section aria-labelledby="netzverbund-title" className="relative">
          <h1
            id="netzverbund-title"
            className="text-3xl sm:text-4xl font-semibold tracking-tight text-gray-900 dark:text-white mb-6"
          >
            {pageContent.h1}
          </h1>
          <div className="space-y-3">
            {pageContent.intro.map((paragraph, index) => (
              <p key={index} className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">
                {paragraph}
              </p>
            ))}
          </div>
        </section>

        {pageContent.sections.map((section) => (
          <ContentSection
            key={section.id}
            id={section.id}
            title={section.title}
            icon={ICONS[section.icon]}
          >
            <SectionBody section={section} />
          </ContentSection>
        ))}

        <section aria-labelledby="netzverbund-background" className="relative">
          <h2
            id="netzverbund-background"
            className="text-lg sm:text-xl font-semibold tracking-tight text-ergo-600 dark:text-ergo-400 mb-3"
          >
            {pageContent.background.title}
          </h2>
          <p className="text-base text-gray-500 dark:text-gray-400 leading-relaxed">{pageContent.background.text}</p>
        </section>
      </main>
    </>
  );
}
